import VocabularyWordCard from "./VocabularyWordCard";

export default function VocabularyTopicPage({
  topic,
  terms = [],
  getTranslationsForTerm,
  getVocabularyProgressForTerm,
  getMasteryLabel,
  getVocabularyTopicStats,
  loadingVocabulary,
  openFlashcards,
  vocabularyLanguage,
  pendingVocabularyLanguage,
  setPendingVocabularyLanguage,
  saveVocabularyLanguage,
  openMatching,
  openQuiz,
  goBack,
  styles,
  titleOverride,
}) {
  // =========================================================
  // TOPIC PROGRESS / MASTERY STATS
  // =========================================================

  const stats = getVocabularyTopicStats
    ? getVocabularyTopicStats()
    : {
        total: terms.length,
        newCount: terms.length,
        learningCount: 0,
        familiarCount: 0,
        masteredCount: 0,
        dueCount: 0,
        overallMasteryPct: 0,
      };

  // =========================================================
  // LOADING
  // =========================================================

  if (loadingVocabulary) {
    return (
      <div style={styles.card}>
        <div style={styles.emptyBox}>
          Loading vocabulary...
        </div>
      </div>
    );
  }

  // =========================================================
  // PAGE
  // =========================================================

  return (
    <div style={styles.card}>
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div style={styles.topBar}>
        <div>
          <div style={styles.quizPath}>
            Vocabulary Topic
          </div>

          <h2 style={styles.sectionTitle}>
            {titleOverride || topic?.topic_name || "Vocabulary"}
          </h2>

          {topic?.description && (
            <div
              style={{
                marginTop: "6px",
                color: "#6b7280",
                lineHeight: "1.5",
              }}
            >
              {topic.description}
            </div>
          )}
        </div>

        <button
          style={styles.secondaryButton}
          onClick={goBack}
        >
          Back
        </button>
      </div>

      {/* =====================================================
          PROGRESS SUMMARY
      ===================================================== */}
      {topic && getVocabularyTopicStats && (
      <div
        style={{
          marginTop: "24px",
          marginBottom: "26px",
        }}
      >
        <h3
          style={{
            marginTop: 0,
            marginBottom: "14px",
          }}
        >
          Your Progress
        </h3>

        {/* Progress cards */}

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(120px, 1fr))",
            gap: "10px",
          }}
        >
          <div style={styles.emptyBox}>
            <strong>Total</strong>

            <div
              style={{
                marginTop: "6px",
                fontSize: "20px",
                fontWeight: "700",
              }}
            >
              {stats.total}
            </div>
          </div>

          <div style={styles.emptyBox}>
            <strong>New</strong>

            <div
              style={{
                marginTop: "6px",
                fontSize: "20px",
                fontWeight: "700",
              }}
            >
              {stats.newCount}
            </div>
          </div>

          <div style={styles.emptyBox}>
            <strong>Learning</strong>

            <div
              style={{
                marginTop: "6px",
                fontSize: "20px",
                fontWeight: "700",
              }}
            >
              {stats.learningCount}
            </div>
          </div>

          <div style={styles.emptyBox}>
            <strong>Familiar</strong>

            <div
              style={{
                marginTop: "6px",
                fontSize: "20px",
                fontWeight: "700",
              }}
            >
              {stats.familiarCount}
            </div>
          </div>

          <div style={styles.emptyBox}>
            <strong>Mastered</strong>

            <div
              style={{
                marginTop: "6px",
                fontSize: "20px",
                fontWeight: "700",
              }}
            >
              {stats.masteredCount}
            </div>
          </div>

          <div style={styles.emptyBox}>
            <strong>Due</strong>

            <div
              style={{
                marginTop: "6px",
                fontSize: "20px",
                fontWeight: "700",
              }}
            >
              {stats.dueCount}
            </div>
          </div>
        </div>

        {/* ===================================================
            OVERALL MASTERY
        =================================================== */}

        <div
          style={{
            marginTop: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <div
              style={{
                fontWeight: "700",
              }}
            >
              Overall Mastery
            </div>

            <div
              style={{
                fontWeight: "700",
              }}
            >
              {stats.overallMasteryPct}%
            </div>
          </div>

          <div
            style={{
              height: "9px",
              backgroundColor: "#e5e7eb",
              borderRadius: "999px",
              overflow: "hidden",
              marginTop: "9px",
            }}
          >
            <div
              style={{
                width: `${stats.overallMasteryPct}%`,
                height: "100%",
                backgroundColor: "#2563eb",
                transition: "width 0.3s ease",
              }}
            />
          </div>
        </div>
      </div>
      )}
      {/* =====================================================
          STUDY FLASHCARDS
      ===================================================== */}

      {topic && openFlashcards && (
        <div
          style={{
            marginBottom: "28px",
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <button
            style={styles.smallPrimaryButton}
            onClick={openFlashcards}
            disabled={terms.length === 0}
          >
            Study Flashcards
          </button>

          <button
            style={styles.secondaryButton}
            onClick={openMatching}
            disabled={terms.length < 2}
          >
            Matching
          </button>

          <button
            style={styles.secondaryButton}
            onClick={openQuiz}
            disabled={terms.length < 4}
          >
            Quiz
          </button>
        </div>
      )}

      {/* =====================================================
          LANGUAGES
      ===================================================== */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
          marginBottom: "28px",
        }}
      >
        <strong>Study Language:</strong>

        <select
          value={pendingVocabularyLanguage}
          onChange={(e) =>
            setPendingVocabularyLanguage(e.target.value)
          }
          style={{
            padding: "9px 12px",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            backgroundColor: "#ffffff",
            fontSize: "14px",
          }}
        >
          <option value="zh">中文</option>
          <option value="es">Español</option>
          <option value="ru">Русский</option>
        </select>

        <button
          style={styles.smallPrimaryButton}
          onClick={saveVocabularyLanguage}
        >
          Save
        </button>

        <span
          style={{
            color: "#6b7280",
            fontSize: "14px",
          }}
        >
          Current:{" "}
          {vocabularyLanguage === "zh"
            ? "中文"
            : vocabularyLanguage === "es"
            ? "Español"
            : "Русский"}
        </span>
      </div>

      {/* =====================================================
          WORD LIST
      ===================================================== */}

      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <h3
            style={{
              margin: 0,
            }}
          >
            Vocabulary
          </h3>

          <div
            style={{
              color: "#6b7280",
              fontSize: "14px",
            }}
          >
            {terms.length} terms
          </div>
        </div>

        {terms.length === 0 ? (
          <div style={styles.emptyBox}>
            No vocabulary terms found for this topic.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gap: "12px",
            }}
          >
            {terms.map((term) => (
              <VocabularyWordCard
                key={term.id}
                term={term}
                translations={
                  getTranslationsForTerm
                    ? getTranslationsForTerm(term.id)
                    : {}
                }
                progress={
                  getVocabularyProgressForTerm
                    ? getVocabularyProgressForTerm(
                        term.id
                      )
                    : null
                }
                getMasteryLabel={getMasteryLabel}
                vocabularyLanguage={vocabularyLanguage}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}